<script lang="ts" setup type="text/x-template" id="editorTemplate">
import AUApi from "@/api";
import { ref, onMounted, reactive, nextTick, getCurrentInstance } from "vue";
import { getSiteData } from "@/NavigationHelper";
import { Globals } from "@/Globals";
import { calcFunctionNew } from "@/CalcHelperStatic";
import { goBack } from "@/NavigationHelper";

import {
  getKennungValueDisplay,
  getKennungValue,
  getKennungTitle,
  getColumnByKennung,
} from "@/DataHelper";
import { mountedBase, BaseSetup, onSave } from "@/DataLoaderStatic";
import {
  begriffBezeichnung,
  checkBegriff,
  globalsBezeichnung,
  globalsKennung,
} from "@/local";
import {
  createEditorValidator,
  provideEditorValidator,
} from "@/components/EditorValidator";
import { showSnackbar } from "@/UIHelper";
const validator = createEditorValidator();
provideEditorValidator(validator);

import CommonBaseButtons from "@/components/common/BaseButtons.vue";

const emit = defineEmits(["back", "save", "delete"]);

let checkDateRange!:
  | ((von: Date, bis: Date, aic: number) => boolean)
  | undefined;
let IsAllDay = ref<boolean>(false);
let color: any = "success";
let stellVertreter: boolean = false;
let showStatusStellvertreter = ref<boolean>(false);
let saveModell = "WEB_PZE_PlanungDoppelbuchungen";
let stellBew = ref<boolean>(false);
let date: any = "";
let date1: any = "";
let allColumns: DataColumn[] = [];
let bewilligung = ref<boolean>(false);
let bewilligt = ref<boolean>(false);
let offen = ref<boolean>(false);
let stellOffen: boolean = false;
let sperrBegriff = ref<string>("");
let bewStell: any = "";
let b2Aic: number = 0;
let b2Titel: string = "";
let aicOffen: number = 0;
let aicEig: number = 0;
let textOffen: string = "";
let step = ref<number>(0);
let resturlaub = ref<string>("");
let loading = ref<boolean>(false);
let loader: any = null;
const siteData = getSiteData();
let initalized = ref<boolean>(false);
let text = ref<string>("");
let existStellvertreter = ref<string>("");

const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_Planung_Dialog",
  emitBack: () => emit("back"),
};
const vueInstance = getCurrentInstance();

const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));
onMounted(async () => {
  initalized.value = false;
  step.value = 1;
  await mountedBase(uiData, pageParams);
  if (!uiData.showLoading && uiData.data) {
    const row = uiData.data[0];
    sperrBegriff.value = globalsKennung(
      Globals.Begriff,
      pageParams.query ?? "",
    );
    text.value = getKennungValueDisplay(row, "ZA", uiData.columns);
    const allDay = getKennungValue(row, "B_CALC_FIELD", uiData.columns);
    if (allDay === true) {
      IsAllDay.value = true;
    }

    const zuBewilligen = getKennungValueDisplay(row, "GRANTED", uiData.columns);

    if (zuBewilligen === "ja") {
      bewilligung.value = true;
    }
    const checkBewilligt = getKennungValueDisplay(row, "b1", uiData.columns);
    if (bewilligung.value) {
      if (checkBewilligt === "bewilligt") {
        bewilligt.value = true;
      } else if (checkBewilligt === "abgelehnt") {
      } else if (checkBewilligt === "offen") {
        offen.value = true;
      }
    }
    const checkStellvertreter = checkBegriff("Web_Stellvertreter");
    if (checkStellvertreter) {
      stellVertreter = true;
      bewStell = getKennungValueDisplay(row, "b2", uiData.columns);
      b2Aic = getKennungValue(row, "b2", uiData.columns);
      b2Titel = getKennungTitle("b2", uiData.columns);
      const column = getColumnByKennung("b2", null, uiData.columns);
      const combo = column?.meta.combo;
      aicEig = column?.aicEig;
      if (combo) {
        aicOffen = combo[0].aic;
        textOffen = combo[0].bezeichnung;
      }
      if (bewStell === "bewilligt") {
        stellBew.value = true;
      } else if (checkBewilligt === "offen") {
        stellOffen = true;
      }
      existStellvertreter.value = getKennungValueDisplay(
        row,
        "SV",
        uiData.columns,
      );
      if (existStellvertreter) {
        showStatusStellvertreter.value = true;
      }
    }
    if (!row.aic_Bew_pool) {
      // ist es ein besteheder Satz dann braucht er nicht prüfen
      if (!checkIntersection(true)) {
        emit("back");
      }
    }
  }
  uiData.showLoading = false;
  uiData.initialized = true;
  initalized.value = true;
});

function showDelete() {
  if (
    uiData.data &&
    uiData.data[0].aic_Bew_pool &&
    uiData.data[0].aic_Bew_pool > 0
  )
    return true;
  return false;
}

function onDelete() {
  if (uiData.data[0] && uiData.data[0].aic_Bew_pool) {
    emit("delete", uiData.data[0].aic_Bew_pool);
  }
}

function checkIntersection(skipChanging: boolean = false) {
  if (checkDateRange) {
    if (vueInstance) {
      const dateRange = vueInstance.proxy?.$refs.dateRange as any;
      if (
        dateRange &&
        (skipChanging || dateRange.changing) &&
        dateRange.von &&
        dateRange.bis
      ) {
        const von = dateRange.von;
        let bis = dateRange.bis;
        if (von.valueOf() === bis.valueOf() && dateRange.IsAllDay) {
          bis = bis.addDays(1);
        }
        if (!checkDateRange(von, bis, uiData.data[0].aic_Bew_pool ?? 0)) {
          return false;
        }
      }
    }
  }
  return true;
}

async function save() {
  // beim Speichern immer prüfen, nicht nur während dateRange.changing
  if (!checkIntersection(true)) {
    showSnackbar({
      text: globalsBezeichnung(
        Globals.Begriff,
        "Ueberschneidung",
        "Überschneidung!",
      ),
      duration: 4000,
      color: "error",
    });
    return;
  }
  const { valid, invalidFields } = await validator.validateAsync();
  if (!valid) {
    showSnackbar({
      text:
        "Validierung fehlgeschlagen - Bitte prüfen! (" +
        invalidFields.join(", ") +
        ")",
      duration: 4000,
      color: "error",
    });
    return;
  }
  const aicAbfAllBez = globalsKennung(Globals.Begriff, "WebPlanungSpeichern");
  const varName = "BewAic";
  const varUbergabe: VarUebergabe[] = [];
  if (uiData.data[0].aic_Bew_pool) {
    const wert = uiData.data[0].aic_Bew_pool;

    varUbergabe.push({
      var: varName,
      type: "double",
      wert,
      art: 2,
      user: AUApi.Current.user.user,
      perm: false,
    });
  }
  let vonModellZeitbereich = AUApi.Current.zeitbereich.von;
  let bisModellZeitbereich = AUApi.Current.zeitbereich.bis;

  const vbColumn = uiData.columns.find((column) => column.kennung === "vb");
  if (vbColumn) {
    const vonBis = uiData.data[0][vbColumn.name];
    // const dateRange = vueInstance?.proxy?.$refs.dateRange as any;

    // ich muss von - bis das erfasst ist als Zeitraum ans modell übergeben!!
    // das kleinste Datum muss genommen werden, das alle Timer zurück
    // gesetzt werden können!
    vonModellZeitbereich = vonBis.von;
    const vonZeitbereich = siteData.zeitbereich.von;
    if (vonModellZeitbereich) {
      if (vonModellZeitbereich > vonZeitbereich) {
        vonModellZeitbereich = vonZeitbereich;
      }
    }
    const dateBis = new Date(vonBis.bis);
    bisModellZeitbereich = dateBis.addDays(-1); // ModellZeitraum muss wieder BIS Inclusive sein!
    let bisZeitbereich = siteData.zeitbereich.bis;
    // siteData BIS ist 1 Tag MEHR!

    bisZeitbereich = new Date(bisZeitbereich).addDays(-1);
    if (bisModellZeitbereich) {
      if (bisModellZeitbereich < bisZeitbereich) {
        bisModellZeitbereich = bisZeitbereich;
      }
    }
  }
  const modellZeitbereich: APIZeitbereich = {
    von: vonModellZeitbereich,
    bis: bisModellZeitbereich,
    bereich: "Tag",
  };
  await calcFunctionNew(
    saveModell,
    AUApi.Current.user.aic,
    false,
    onCalculated,
    modellZeitbereich,
    varUbergabe,
  )?.then((response: any) => {
    if (response !== "Error" && response?.msgType !== "Error") {
      if (aicAbfAllBez) {
        const aicAbfAll = aicAbfAllBez.aic;
        onSave(uiData, pageParams, aicAbfAll, "", modellZeitbereich)?.then(
          () => {
            emit("save");
          },
        );
      } else {
        showSnackbar({
          text: "Abfrage nicht gefunden",
          color: "error",
        });
        uiData.showLoading = false;
        return;
      }
    }
    return;
  });
}
function onCalculated(args?: any) {
  // emit("save");
}
function checkValue(evt: any) {
  bewStell = textOffen;
  stellOffen = true;
  const spalte = "e" + aicEig;
  for (const i in uiData.data) {
    if (i === spalte) {
      uiData.data[i].aic = aicOffen; // STMM wir können in eine Zeile keine nummer schreiben
    }
  }
}
function calcSalden() {
  loading.value = true;

  const modell = "Web_Resturlaub_per_Stichtag";
  let begriff: any;
  if (modell) {
    begriff = globalsKennung(Globals.Begriff, modell);
  }
  if (!begriff) {
    showSnackbar({
      text: "Modell nicht gefunden:" + modell,
      color: "error",
    });
    uiData.showLoading = false;
    return;
  }
  AUApi.Current.postCalc(
    [],
    [],
    begriff.aic,
    pageParams.aic,
    pageParams.zeitbereich,
    0,
    0,
    "",
    successCalc,
    begriff.maxB,
  );
}
function successCalc(res: any) {
  resturlaub = res.data.ergebnis;
  loading.value = false;
  step.value++;
  return;
}
</script>

<template id="editorTemplate">
  <v-card class="pa-0">
    <v-window v-model="step">
      <v-window-item :value="1">
        <v-card
          v-if="!uiData.showLoading"
          class="dvhDialog"
          :loading="uiData.showLoading"
        >
          <v-toolbar
            class="subtitle-1 dvhBackground lighten-2 pl-2"
            v-if="initalized"
            style="color: white"
          >
            <span style="color: white">
              <v-icon v-if="bewilligung && bewilligt && stellBew"
                >mdi-check</v-icon
              >
              <v-icon class="white" v-if="bewilligung && bewilligt && !stellBew"
                >mdi-cancel</v-icon
              >
              <v-icon
                class="white"
                v-if="bewilligung && !bewilligt && !stellBew && !offen"
                >mdi-checkbox-blank-outline</v-icon
              >
              <v-icon class="white" v-if="bewilligung && offen"
                >mdi-checkbox-blank-outline</v-icon
              >

              {{ text }}
            </span>

            <v-spacer></v-spacer>

            <span style="color: white" v-if="showStatusStellvertreter">
              <v-icon class="white" v-if="stellBew" size="small"
                >mdi-check</v-icon
              >
              <v-icon class="white" v-if="!stellBew" size="small"
                >mdi-cancel</v-icon
              >
              <v-icon class="white" v-if="!stellOffen && !stellBew" size="small"
                >mdi-checkbox-blank-outline</v-icon
              >
              {{ existStellvertreter }}
            </span>
            <v-divider class="px-2" vertical></v-divider>
            <v-btn @click="goBack()" icon="mdi-close"> </v-btn>
          </v-toolbar>

          <v-card-text class="pb-0" v-if="initalized">
            <EditComboBox
              kennung="ZA"
              id="EventType"
              :name="getKennungTitle('Zeitart', uiData.columns)"
              style="width: 100%"
              :autofocus="true"
            />

            <EditDateRange2
              ref="dateRange"
              kennung="vb"
              :IsAllDay="IsAllDay"
              v-if="sperrBegriff != null"
              :sperrBegriff="sperrBegriff"
              @seteditvalue="checkValue"
              :showGanztag="true"
            ></EditDateRange2>

            <EditComboBox
              v-if="stellVertreter"
              kennung="SV"
              id="SV"
              :name="getKennungTitle('Stellvertreter', uiData.columns)"
              style="width: 100%"
              @seteditvalue="checkValue"
            />

            <EditTextArea id="Description" kennung="MEMO" name="Bemerkung" />

            <EditTextArea id="VG" kennung="VG" name="Bemerkung" />
          </v-card-text>
          <v-card-actions class="py-0">
            <v-spacer></v-spacer>
            <CommonBaseButtons
              :saveVisible="true"
              :createVisible="false"
              :refreshVisible="false"
              :backVisible="false"
              :deleteVisible="showDelete()"
              @delete="onDelete"
              @save="save"
              @back="goBack()"
            ></CommonBaseButtons>
          </v-card-actions>
        </v-card>
      </v-window-item>
      <v-window-item :value="2">
        <v-card
          class="dvhDialog"
          :title="
            globalsBezeichnung(
              Globals.Begriff,
              'Web_Berechnung_Resturlaub',
              'Berechnung Resturlaub per Stichtag',
            )
          "
        >
          <!-- <v-card-title
            v-if="resturlaub"
            :text="
              globalsBezeichnung(
                Globals.Begriff,
                'Web_Text_Anspruch',
                'Resturlaub'
              )
            "
          >
          </v-card-title> -->
          <v-card-text style="white-space: pre" v-if="resturlaub">
            {{ begriffBezeichnung("Web_Text_Anspruch") }}
            {{ resturlaub }}
          </v-card-text></v-card
        >
      </v-window-item>
    </v-window>
    <v-divider color="warning"></v-divider>

    <v-card-actions>
      <v-btn
        v-if="step > 1"
        :disabled="step === 1"
        variant="text"
        @click="step--"
        icon="mdi-backspace"
      >
      </v-btn>

      <v-btn
        block
        v-if="step !== 2"
        @click="calcSalden()"
        :disabled="step === 2"
        color="primary"
        variant="tonal"
        :loading="loading"
        prepend-icon="mdi-play-circle"
      >
        {{
          globalsBezeichnung(Globals.Begriff, "Web_Text_Anspruch", "Resturlaub")
        }}
        <template v-slot:loader>
          <span class="custom-loader">
            <v-icon light>mdi-cached</v-icon>
          </span>
        </template>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.fullcol {
  width: 100%;
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 0px;
  margin: 0px;
}
.dvhDialog {
  padding: 0px;
}
/* @media (max-width: 550px) {
  .dvhDialog {
    padding: 0px;
    width: 300px;
  }
} */

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}
@-moz-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-webkit-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-o-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
